import React, { useState, useMemo } from 'react';
import { MaterialCatalog, MaterialCategory, StockLevel } from '@models/warehouse.model';
import { StockStatus } from '@components/atoms/StockBadge';
import { Loading } from '@components/atoms/Loading';
import { Tab } from '@components/atoms/Tab';
import { EmptyData } from '@components/molecules/EmptyData';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { SingleValue, MultiValue } from 'react-select';
import EditIcon from '@mui/icons-material/Edit';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import TuneIcon from '@mui/icons-material/Tune';
import WarningIcon from '@mui/icons-material/Warning';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SearchIcon from '@mui/icons-material/Search';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Badge color map matching CategoryCard color names
const badgeColors: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-700' },
  green: { bg: 'bg-green-100', text: 'text-green-700' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  red: { bg: 'bg-red-100', text: 'text-red-700' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-700' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-700' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
};
const defaultBadge = { bg: 'bg-gray-100', text: 'text-gray-700' };

// Combined type for material with stock information
export interface MaterialWithStock extends MaterialCatalog {
  stockLevel?: StockLevel;
  totalOnHand: number;
  totalReserved: number;
  totalAvailable: number;
  inventoryValue: number;
  stockStatus: StockStatus;
}

interface MaterialCatalogTableProps {
  materials: MaterialCatalog[];
  stockLevels: StockLevel[];
  categories: MaterialCategory[];
  isLoading?: boolean;
  onEdit?: (material: MaterialCatalog) => void;
  onReceiveStock?: (material: MaterialCatalog) => void;
  onAdjustStock?: (material: MaterialCatalog) => void;
  onAddMaterial?: () => void;
}

type FilterTab = 'all' | 'low' | 'out';

const ITEMS_PER_PAGE = 10;

export const MaterialCatalogTable: React.FC<MaterialCatalogTableProps> = ({
  materials,
  stockLevels,
  categories,
  isLoading = false,
  onEdit,
  onReceiveStock,
  onAdjustStock,
  onAddMaterial,
}) => {
  // Filter states
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);

  // Combine materials with their stock information
  const materialsWithStock = useMemo<MaterialWithStock[]>(() => {
    return materials.map((material) => {
      // Find all stock levels for this material across warehouses
      const materialStockLevels = stockLevels.filter(
        (sl) => sl.catalogId === material.catalogId
      );

      const totalOnHand = materialStockLevels.reduce(
        (sum, sl) => sum + (sl.quantityOnHand || 0),
        0
      );
      const totalReserved = materialStockLevels.reduce(
        (sum, sl) => sum + (sl.quantityReserved || 0),
        0
      );
      const totalAvailable = totalOnHand - totalReserved;
      const avgCost = material.averageCost || 0;
      const inventoryValue = totalOnHand * avgCost;

      // Determine stock status
      let stockStatus: StockStatus = 'in_stock';
      if (totalAvailable <= 0) {
        stockStatus = 'out_of_stock';
      } else if (material.reorderPoint && totalAvailable <= material.reorderPoint) {
        stockStatus = 'low_stock';
      }

      return {
        ...material,
        stockLevel: materialStockLevels[0], // Primary stock level
        totalOnHand,
        totalReserved,
        totalAvailable,
        inventoryValue,
        stockStatus,
      };
    });
  }, [materials, stockLevels]);

  // Apply filters
  const filteredMaterials = useMemo(() => {
    return materialsWithStock.filter((material) => {
      // Tab filter
      if (activeTab === 'low' && material.stockStatus !== 'low_stock') return false;
      if (activeTab === 'out' && material.stockStatus !== 'out_of_stock') return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = material.name.toLowerCase().includes(query);
        const matchesSku = material.sku?.toLowerCase().includes(query);
        if (!matchesName && !matchesSku) return false;
      }

      // Category filter
      if (selectedCategoryId && material.categoryId !== selectedCategoryId) {
        return false;
      }

      return true;
    });
  }, [materialsWithStock, activeTab, searchQuery, selectedCategoryId]);

  // Pagination
  const totalPages = Math.ceil(filteredMaterials.length / ITEMS_PER_PAGE);
  const paginatedMaterials = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredMaterials.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMaterials, currentPage]);

  // Stats
  const stats = useMemo(() => {
    return {
      total: materials.length,
      lowStock: materialsWithStock.filter((m) => m.stockStatus === 'low_stock').length,
      outOfStock: materialsWithStock.filter((m) => m.stockStatus === 'out_of_stock').length,
    };
  }, [materials.length, materialsWithStock]);

  // Get category name
  const getCategoryName = (categoryId?: string) => {
    if (!categoryId) return null;
    return categories.find((c) => c.categoryId === categoryId);
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  // Format number with commas
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Reset page when filters change
  const handleTabChange = (tab: FilterTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setCurrentPage(1);
  };

  if (isLoading) {
    return <Loading size="xl" center />;
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Items</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Inventory2Icon className="text-indigo-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Low Stock</p>
              <p className="text-2xl font-semibold text-amber-600">{stats.lowStock}</p>
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <WarningIcon className="text-amber-600" sx={{ fontSize: 20 }} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Actions Bar */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Tabs */}
          <Tab
            tabs={[
              { id: 'all', name: 'All Materials' },
              { id: 'low', name: 'Low Stock', count: stats.lowStock > 0 ? stats.lowStock : undefined },
              { id: 'out', name: 'Out of Stock', count: stats.outOfStock > 0 ? stats.outOfStock : undefined },
            ]}
            activeTab={activeTab}
            onTabChange={(tabId) => handleTabChange(tabId as FilterTab)}
            variant="underline"
          />

          <div className="flex-1" />

          {/* Search */}
          <div className="relative">
            <SearchIcon
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              sx={{ fontSize: 18 }}
            />
            <input
              type="text"
              placeholder="Search by name or SKU..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-64 border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Category Filter */}
          <div className="w-48">
            <ReactSelect
              options={categories.map((cat) => ({ value: cat.categoryId, label: cat.name }))}
              value={
                selectedCategoryId
                  ? { value: selectedCategoryId, label: categories.find((c) => c.categoryId === selectedCategoryId)?.name || '' }
                  : null
              }
              onChange={(option: SingleValue<SelectOption> | MultiValue<SelectOption>) => {
                if (option && !Array.isArray(option) && 'value' in option) {
                  handleCategoryChange(option.value as string);
                } else {
                  handleCategoryChange('');
                }
              }}
              placeholder="All Categories"
              isClearable
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 overflow-hidden">
        {filteredMaterials.length === 0 ? (
          <EmptyData
            description={searchQuery || selectedCategoryId || activeTab !== 'all'
              ? "No materials found. Try adjusting your filters."
              : "No materials found. Add your first material to get started."}
            textButton={!searchQuery && !selectedCategoryId && activeTab === 'all' ? "Add Material" : undefined}
            onClickButton={onAddMaterial}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Material
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      On Hand
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Available
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Avg Cost
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedMaterials.map((material) => {
                    const category = getCategoryName(material.categoryId);
                    const isLowStock = material.stockStatus === 'low_stock';
                    const isOutOfStock = material.stockStatus === 'out_of_stock';

                    return (
                      <tr
                        key={material.catalogId}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          isLowStock ? 'bg-amber-50' : ''
                        } ${isOutOfStock ? 'bg-red-50' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                isLowStock
                                  ? 'bg-amber-100'
                                  : isOutOfStock
                                  ? 'bg-red-100'
                                  : 'bg-gray-100'
                              }`}
                            >
                              {isLowStock || isOutOfStock ? (
                                <WarningIcon
                                  className={isOutOfStock ? 'text-red-600' : 'text-amber-600'}
                                  sx={{ fontSize: 16 }}
                                />
                              ) : (
                                <Inventory2Icon className="text-gray-600" sx={{ fontSize: 16 }} />
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{material.name}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {material.sku || ''}{material.sku && material.defaultSupplier ? ' · ' : ''}{material.defaultSupplier || ''}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {category ? (
                            <span
                              className={`inline-block max-w-[120px] truncate px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                (badgeColors[category.color?.toLowerCase() || ''] || defaultBadge).bg
                              } ${
                                (badgeColors[category.color?.toLowerCase() || ''] || defaultBadge).text
                              }`}
                              title={category.name}
                            >
                              {category.name}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm text-right font-medium ${
                            isLowStock
                              ? 'text-amber-600'
                              : isOutOfStock
                              ? 'text-red-600'
                              : 'text-gray-900'
                          }`}
                        >
                          {formatNumber(material.totalOnHand)}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm text-right font-medium ${
                            isLowStock
                              ? 'text-amber-600'
                              : isOutOfStock
                              ? 'text-red-600'
                              : 'text-gray-900'
                          }`}
                        >
                          {formatNumber(material.totalAvailable)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">
                          {formatCurrency(material.averageCost || 0)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                          {formatCurrency(material.inventoryValue)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit?.(material);
                              }}
                              className="p-1 text-gray-400 hover:text-indigo-600 rounded"
                              title="Edit"
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </button>
                            <button type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onReceiveStock?.(material);
                              }}
                              className={`p-1 rounded ${
                                isLowStock || isOutOfStock
                                  ? 'text-amber-500 hover:text-amber-600'
                                  : 'text-gray-400 hover:text-green-600'
                              }`}
                              title={isLowStock || isOutOfStock ? 'Order now' : 'Receive stock'}
                            >
                              <AddShoppingCartIcon sx={{ fontSize: 18 }} />
                            </button>
                            <button type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onAdjustStock?.(material);
                              }}
                              className="p-1 text-gray-400 hover:text-amber-600 rounded"
                              title="Adjust stock"
                            >
                              <TuneIcon sx={{ fontSize: 18 }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing{' '}
                  <span className="font-medium text-gray-900">
                    {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium text-gray-900">
                    {Math.min(currentPage * ITEMS_PER_PAGE, filteredMaterials.length)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-gray-900">
                    {filteredMaterials.length}
                  </span>{' '}
                  materials
                </div>
                <div className="flex items-center gap-2">
                  <button type="button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button type="button"
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                          currentPage === pageNum
                            ? 'bg-indigo-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button type="button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MaterialCatalogTable;
